import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { HStack, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Select } from '../../../components/primitives/Select';
import { Sheet } from '../../../components/primitives/Sheet';
import { Caption, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { tokens } from '../../../theme/tokens';
import { useVoiceAgents } from '../hooks';
import { buildAgentOptions } from '../utils/agentOptionLabels';

interface BatchCallAgentModalProps {
  visible: boolean;
  organizationId: string;
  leadCount: number;
  onConfirm: (voiceAgentId: string) => void;
  onClose: () => void;
}

export function BatchCallAgentModal({
  visible,
  organizationId,
  leadCount,
  onConfirm,
  onClose,
}: Readonly<BatchCallAgentModalProps>) {
  const { t } = useTranslation();
  const { isDesktop, platform } = useResponsive();
  const [agentId, setAgentId] = useState('');
  const wasVisibleRef = useRef(visible);

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      setAgentId('');
    }
    wasVisibleRef.current = visible;
  }, [visible]);

  const agentsQuery = useVoiceAgents(organizationId, { status: 'published' });

  // FLOSEAI-656 — picking the wrong agent here assigns it to a whole batch of
  // leads, so same-named agents must be distinguishable.
  const agentOptions = useMemo(() => buildAgentOptions(agentsQuery.data ?? []), [agentsQuery.data]);

  const handleConfirm = useCallback(() => {
    if (!agentId.trim()) return;
    onConfirm(agentId.trim());
  }, [agentId, onConfirm]);

  const position = platform === 'web' && isDesktop ? 'center' : 'bottom';
  const title = t('voiceAgents.batchCall.title', { count: leadCount });

  return (
    <Sheet visible={visible} onClose={onClose} title={title} position={position} size="md">
      <View accessibilityLabel={title}>
        <VStack spacing="md">
          <VStack spacing="xs">
            <Label style={styles.inputLabel}>{t('voiceAgents.callsList.agentSelectLabel')}</Label>
            <Select
              variant="dropdown"
              options={agentOptions}
              value={agentId}
              onChange={setAgentId}
              placeholder={t('voiceAgents.callsList.selectAgentPlaceholder')}
              testID="batch-call-agent-select"
              searchable
              searchPlaceholder={t('common.selectSearchPlaceholder')}
              emptySearchLabel={t('common.selectNoMatches')}
            />
            {!agentsQuery.isLoading && agentOptions.length === 0 ? (
              <Caption style={styles.errorText}>
                {t('voiceAgents.callsList.noPublishedAgents')}
              </Caption>
            ) : null}
          </VStack>
        </VStack>
        <HStack
          spacing="md"
          style={{ marginTop: tokens.spacing.lgPlus, justifyContent: 'flex-end' }}
        >
          <Button
            variant="outline"
            size="sm"
            onPress={onClose}
            accessibilityLabel={t('voiceAgents.callsList.cancel')}
          >
            {t('voiceAgents.callsList.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onPress={handleConfirm}
            accessibilityLabel={t('voiceAgents.batchCall.confirm')}
            accessibilityState={{ disabled: !agentId.trim() }}
            disabled={!agentId.trim()}
          >
            {t('voiceAgents.batchCall.confirm')}
          </Button>
        </HStack>
      </View>
    </Sheet>
  );
}

const styles = {
  inputLabel: {
    fontSize: tokens.fontSize.sm,
  },
  errorText: {
    marginTop: tokens.spacing.xs,
    fontSize: tokens.fontSize.smPlus,
  },
} as const;
